import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

const PICK_LABEL = { home: "1", draw: "X", away: "2" };

export default function Betting() {
  const { setCharacter } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("matches");
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [slip, setSlip] = useState(null); // {match, pick, odds}
  const [stake, setStake] = useState(500);
  const [countdown, setCountdown] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, b] = await Promise.all([api.get("/betting/matches"), api.get("/betting/mine")]);
      setData(m.data); setCountdown(m.data.closes_in); setMine(b.data.bets);
      setStake(m.data.min_stake);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => setCountdown((s) => {
      if (s <= 1) { load(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [load]);

  const openSlip = (match, pick) => { setSlip({ match, pick, odds: match.odds[pick] }); setStake(data.min_stake); };

  const placeBet = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/betting/bet", { match_index: slip.match.index, pick: slip.pick, stake: Number(stake), round: data.round });
      setCharacter(res.character);
      toast.success(`Bet placed! ${naira(stake)} on ${PICK_LABEL[slip.pick]} @ ${slip.odds}`);
      setSlip(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const mm = String(Math.floor(countdown / 60)).padStart(2, "0");
  const ss = String(countdown % 60).padStart(2, "0");

  if (!data) return null;

  return (
    <div className="px-5 pt-6" data-testid="betting-page">
      <div className="flex items-center gap-3">
        <button data-testid="bet-back" onClick={() => nav("/city")} className="p-2 rounded-full bg-slate-100"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl">Viewing <span className="text-[#00E5FF]">Centre</span></h1>
          <p className="label-xs text-slate-500">Oga Sunday's spot · bet Game Naira on the matches</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between bg-white border border-slate-200 rounded-xl p-3">
        <span className="text-[#16A34A] font-display" data-testid="bet-wallet">{naira(data.wallet)}</span>
        <span className="label-xs text-[#FFD700] flex items-center gap-1"><Clock size={12} /> Round closes {mm}:{ss}</span>
      </div>

      <p className="mt-2 text-[0.65rem] text-slate-400 leading-tight">⚠️ Virtual Game Naira only — no real money, not withdrawable. Just street fun.</p>

      <div className="mt-3 flex gap-1.5 bg-white border border-slate-200 rounded-full p-1">
        {["matches", "slips"].map((t) => (
          <button key={t} data-testid={`bet-tab-${t}`} onClick={() => setTab(t)}
            className={`flex-1 py-2 rounded-full label-xs capitalize ${tab === t ? "bg-[#00E5FF] text-slate-900" : "text-slate-500"}`}>{t === "slips" ? "My Slips" : "Matches"}</button>
        ))}
      </div>

      <div className="mt-4 space-y-3 mb-4">
        {tab === "matches" && data.matches.map((m) => (
          <motion.div key={m.index} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`match-${m.index}`} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center justify-center gap-3 text-sm font-semibold">
              <span className="flex-1 text-right truncate">{m.home}</span>
              <span className="text-slate-400 text-xs">vs</span>
              <span className="flex-1 truncate">{m.away}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {["home", "draw", "away"].map((p) => (
                <button key={p} data-testid={`odds-${m.index}-${p}`} onClick={() => openSlip(m, p)}
                  className="bg-slate-100 border border-slate-200 hover:border-[#00E5FF] rounded-lg py-2 transition-colors">
                  <div className="label-xs text-slate-500">{p === "home" ? "Home (1)" : p === "draw" ? "Draw (X)" : "Away (2)"}</div>
                  <div className="font-display text-[#00E5FF]">{m.odds[p]}</div>
                </button>
              ))}
            </div>
          </motion.div>
        ))}

        {tab === "slips" && mine.length === 0 && <p className="text-center text-sm text-slate-400 py-12">No bets yet. Back a winner!</p>}
        {tab === "slips" && mine.map((b) => (
          <div key={b.id} data-testid={`slip-${b.id}`} className="bg-white border border-slate-200 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold truncate">{b.home} v {b.away}</span>
              <span className={`label-xs font-bold ${b.status === "won" ? "text-[#16A34A]" : b.status === "lost" ? "text-[#FF0055]" : "text-[#FFD700]"}`}>{b.status.toUpperCase()}</span>
            </div>
            <div className="mt-1 label-xs text-slate-500">
              Pick {PICK_LABEL[b.pick]} @ {b.odds} · stake {naira(b.stake)}
              {b.status === "open" ? " · awaiting result" : b.status === "won" ? ` · won ${naira(b.payout)} 🎉` : ` · result ${PICK_LABEL[b.result] || "-"}`}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {slip && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/60" onClick={() => setSlip(null)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="fixed left-1/2 -translate-x-1/2 bottom-28 z-50 w-[88%] max-w-sm bg-white border border-slate-200 rounded-2xl p-5" data-testid="bet-slip">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg">Bet Slip</h3>
                <button data-testid="slip-close" onClick={() => setSlip(null)} className="p-1.5 rounded-full bg-slate-100"><X size={16} /></button>
              </div>
              <p className="text-sm text-slate-500">{slip.match.home} v {slip.match.away}</p>
              <p className="label-xs text-slate-500 mt-1">Pick <span className="text-[#00E5FF]">{PICK_LABEL[slip.pick]}</span> @ <span className="text-[#00E5FF]">{slip.odds}</span></p>
              <input data-testid="slip-stake" type="number" min={data.min_stake} value={stake} onChange={(e) => setStake(e.target.value)}
                className="mt-3 w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#00E5FF] outline-none" />
              <div className="mt-2 flex items-center justify-between label-xs">
                <span className="text-slate-500">Potential win</span>
                <span className="text-[#16A34A] font-display">{naira(Math.floor((Number(stake) || 0) * slip.odds))}</span>
              </div>
              <button data-testid="slip-confirm" disabled={busy} onClick={placeBet}
                className="mt-4 w-full py-3 rounded-xl font-bold bg-[#00E5FF] text-slate-900 disabled:opacity-50">Place Bet</button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
